import { useState } from 'react';
import { Box, Typography, CircularProgress, Container } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useGetAuthUserQuery } from 'redux/apiSlices/authUser.slice';
import { useGetUserProjectsQuery, useAddNewProjectMutation } from 'redux/apiSlices/project.slice';
import { useAddTaskMutation } from 'redux/apiSlices/tasks.slice';
import {
  useAddNewDocMutation,
  useAddBlankCustomDocMutation,
  useUpdateCustomDocContentMutation,
  useRenameDocItemMutation,
} from 'redux/apiSlices/doc.slice';
import Button from 'components/Base/Button';
import { PrivateRoutesPaths } from 'routes/Routes.types';
import { IProjectTemplate } from 'data/templates/projectTemplates';
import { TAddEditNewProjectForm } from './components/AddEditNewProjectModal/AddEditNewProjectForm';
import AllProjects from './AllProjects';
import CreateProjectModal from './components/CreateProjectModal';
import emptyProjectImage from 'assets/images/empty_projects.svg';
import { getBoardColumns, addBoardWithoutColumns, createBoardColumn } from 'firebaseServices/queryFunctions/boardQueryFunctions';
import { getProjectAllDocs } from 'firebaseServices/queryFunctions/docsQueryFunctions';

// Helper to convert markdown-style text to EditorJS format
const convertToEditorJsFormat = (text: string) => {
  const lines = text.split('\n');
  const blocks: any[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i++;
      continue;
    }

    // Heading 1
    if (line.startsWith('# ')) {
      blocks.push({
        type: 'header',
        data: { text: line.slice(2), level: 1 },
      });
    }
    // Heading 2
    else if (line.startsWith('## ')) {
      blocks.push({
        type: 'header',
        data: { text: line.slice(3), level: 2 },
      });
    }
    // Heading 3
    else if (line.startsWith('### ')) {
      blocks.push({
        type: 'header',
        data: { text: line.slice(4), level: 3 },
      });
    }
    // List item
    else if (line.startsWith('- ')) {
      const items: string[] = [line.slice(2)];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('- ')) {
        i++;
        items.push(lines[i].trim().slice(2));
      }
      blocks.push({
        type: 'list',
        data: { style: 'unordered', items },
      });
    }
    // Numbered list
    else if (/^\d+\.\s/.test(line)) {
      const items: string[] = [line.replace(/^\d+\.\s/, '')];
      while (i + 1 < lines.length && /^\d+\.\s/.test(lines[i + 1].trim())) {
        i++;
        items.push(lines[i].trim().replace(/^\d+\.\s/, ''));
      }
      blocks.push({
        type: 'list',
        data: { style: 'ordered', items },
      });
    }
    // Table (simple detection)
    else if (line.startsWith('|')) {
      // Skip table for now, convert to paragraph
      blocks.push({
        type: 'paragraph',
        data: { text: line },
      });
    }
    // Regular paragraph
    else {
      blocks.push({
        type: 'paragraph',
        data: { text: line },
      });
    }

    i++;
  }

  return {
    time: Date.now(),
    blocks,
    version: '2.28.2',
  };
};

// Helper to wait
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const Projects = () => {
  const navigate = useNavigate();
  const { data: userData } = useGetAuthUserQuery();
  const userProjectIds = Object.keys(userData?.projects || {});
  const [open, setOpen] = useState(false);

  const { data, isLoading, refetch } = useGetUserProjectsQuery(userProjectIds, {
    refetchOnMountOrArgChange: true,
  });

  const [addNewProject, { isLoading: createLoading }] = useAddNewProjectMutation();
  const [addTask] = useAddTaskMutation();
  const [addNewDoc] = useAddNewDocMutation();
  const [addBlankCustomDoc] = useAddBlankCustomDocMutation();
  const [updateCustomDocContent] = useUpdateCustomDocContentMutation();
  const [renameDocItem] = useRenameDocItemMutation();

  const isEmpty = !isLoading && !(data || []).length;

  const handleCreateProject = async (
    values: TAddEditNewProjectForm,
    template: IProjectTemplate | null
  ) => {
    console.log('=== handleCreateProject called ===');
    console.log('Values:', values);
    console.log('Template:', template);
    console.log('Template ID:', template?.id);
    console.log('Template boards:', template?.boards);

    // Create the project
    await addNewProject(values).unwrap();

    // Wait for Firestore to propagate the new project
    await wait(1000);

    // Refetch to get the new project with retry logic
    let newProject = null;
    for (let attempt = 0; attempt < 5; attempt++) {
      const result = await refetch();
      console.log('Refetch attempt', attempt + 1, 'result:', result.data?.length, 'projects');
      newProject = result.data?.find(p => p.name === values.name);
      if (newProject) {
        break;
      }
      await wait(500);
    }
    console.log('New project found:', newProject);

    if (newProject && template && template.id !== 'blank') {
      console.log('=== Starting template creation ===');
      const userId = userData?.id || '';

      // Create boards from template using direct Firebase calls
      for (const boardTemplate of template.boards) {
        try {
          // Create board WITHOUT default columns
          const board = await addBoardWithoutColumns({
            name: boardTemplate.name,
            projectId: newProject.id,
          });

          console.log('Created board:', board.documentId, board.name);

          // Create columns for the board directly
          for (let i = 0; i < boardTemplate.columns.length; i++) {
            const col = boardTemplate.columns[i];
            await createBoardColumn({
              title: col.title,
              boardId: board.documentId,
              projectId: newProject.id,
              order: i,
              color: col.color,
              bySystem: true,
            });
            console.log('Created column:', col.title);
          }

          // Wait for Firestore to process the columns
          await wait(1000);

          // Fetch columns directly from Firebase
          const columns = await getBoardColumns({
            projectId: newProject.id,
            boardId: board.documentId,
          });

          console.log('Fetched columns:', columns?.length, columns?.map(c => c.title));

          if (columns && columns.length > 0) {
            // Create tasks for each column
            for (const columnData of columns) {
              // Find the matching template column by title
              const templateColumn = boardTemplate.columns.find(
                tc => tc.title === columnData.title
              );

              if (templateColumn && templateColumn.tasks && templateColumn.tasks.length > 0) {
                console.log('Creating tasks for column:', columnData.title, templateColumn.tasks);

                // Create tasks for this column
                for (let taskIndex = 0; taskIndex < templateColumn.tasks.length; taskIndex++) {
                  const taskTitle = templateColumn.tasks[taskIndex];
                  try {
                    await addTask({
                      task: {
                        title: taskTitle,
                        columnId: columnData.documentId,
                        boardId: board.documentId,
                        projectId: newProject.id,
                        order: taskIndex,
                      },
                    }).unwrap();
                    console.log('Created task:', taskTitle);
                  } catch (taskErr) {
                    console.error('Error creating task:', taskErr);
                  }
                }
              }
            }
          } else {
            console.log('No columns found for board:', board.documentId);
          }
        } catch (err) {
          console.error('Error creating board from template:', err);
        }
      }

      // Create docs from template
      for (const docTemplate of template.docs) {
        try {
          // Create the doc container
          await addNewDoc({
            projectId: newProject.id,
            name: docTemplate.name,
          }).unwrap();

          // Wait for Firestore to process
          await wait(1000);

          // Fetch docs directly from Firebase (bypass RTK cache)
          const docs = await getProjectAllDocs(newProject.id);

          console.log('Fetched docs:', docs);

          const newDoc = docs?.find(d => d.name === docTemplate.name);

          if (newDoc && docTemplate.items.length > 0) {
            // Create custom docs inside
            for (const item of docTemplate.items) {
              try {
                console.log('Creating doc item:', item.title);

                // Create blank custom doc
                const customDocId = await addBlankCustomDoc({
                  projectId: newProject.id,
                  docId: newDoc.documentId,
                  location: 'root',
                  creatorId: userId,
                }).unwrap();

                console.log('Created custom doc with ID:', customDocId);

                // Wait a bit for the doc to be created
                await wait(500);

                // Rename the doc to the template title
                await renameDocItem({
                  projectId: newProject.id,
                  docId: newDoc.documentId,
                  docItemId: customDocId,
                  newName: item.title,
                }).unwrap();

                // Update the content
                const editorContent = convertToEditorJsFormat(item.content);
                await updateCustomDocContent({
                  projectId: newProject.id,
                  docId: newDoc.documentId,
                  customDocId,
                  content: editorContent,
                }).unwrap();

                console.log('Doc item created successfully:', item.title);
              } catch (docErr) {
                console.error('Error creating doc item:', docErr);
              }
            }
          }
        } catch (err) {
          console.error('Error creating doc from template:', err);
        }
      }

      // Navigate to the new project
      console.log('=== Template creation complete, navigating ===');
      navigate(`/${PrivateRoutesPaths.Projects}/${newProject.id}`);
    } else if (newProject) {
      console.log('=== No template or blank template, navigating ===');
      navigate(`/${PrivateRoutesPaths.Projects}/${newProject.id}`);
    } else {
      console.log('=== No project found after creation ===');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: 'background.default',
        py: 4,
        px: 3,
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {/* Header with title and new project button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            Projects
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{ px: 2.5 }}
          >
            New project
          </Button>
        </Box>

        {/* Content */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress sx={{ color: 'primary.main' }} />
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 8,
            }}
          >
            <Box
              component="img"
              src={emptyProjectImage}
              alt="No projects yet"
              sx={{
                width: 140,
                height: 140,
                mb: 3,
                opacity: 0.7,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1,
              }}
            >
              No projects yet
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 3,
              }}
            >
              Create your first project to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Create a project
            </Button>
          </Box>
        ) : (
          <AllProjects projectData={data || []} />
        )}
      </Container>

      <CreateProjectModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleCreateProject}
        isLoading={createLoading}
      />
    </Box>
  );
};

export default Projects;
